"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function VisitFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="222189" className="p-8 border border-border bg-card text-center space-y-4 rounded-sm shadow-md">
        <h3 className="text-2xl font-default text-foreground">Hvala vam!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm border-primary text-primary hover:bg-primary/5">
          Pošaljite novi upit
        </Button>
      </div>;
  }
  const customInputClass = "bg-background border-border focus-visible:border-primary focus-visible:ring-primary/20 rounded-sm font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Ime i prezime je obavezno"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Ime i prezime</FormLabel>
              <FormControl>
                <Input placeholder="Marko Marković" className={customInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs">Unesite vaše puno ime i prezime.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email adresa je obavezna",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Nevažeća email adresa"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Email adresa</FormLabel>
              <FormControl>
                <Input type="email" placeholder="marko@primjer.hr" className={customInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs">Na ovu adresu ćemo poslati potvrdu termina.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Nevažeći format telefona"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Broj telefona</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+385 91 123 4567" className={customInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs">Za brzu koordinaciju oko servisa.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="visit_date" rules={{
        required: "Željeni datum je obavezan"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Željeni datum servisa</FormLabel>
              <FormControl>
                <Input type="date" className={customInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs">Odaberite kada želite dovesti romobil.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="guest_count" rules={{
      required: "Broj uređaja je obavezan",
      min: {
        value: 1,
        message: "Minimalno 1 uređaj"
      },
      max: {
        value: 20,
        message: "Maksimalno 20 uređaja"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default font-semibold">Broj romobila</FormLabel>
            <FormControl>
              <Input type="number" min="1" max="20" placeholder="1" className={customInputClass} {...field} />
            </FormControl>
            <FormDescription className="text-xs">Koliko romobila donosite na servis?</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default font-semibold">Model romobila i opis kvara</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[120px] w-full rounded-sm border border-border px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-sans", customInputClass)} placeholder="Npr. Xiaomi M365, problem s baterijom ili probušena guma..." {...field} />
            </FormControl>
            <FormDescription className="text-xs">Navedite što više detalja za točniju procjenu.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto px-8 py-6 text-lg rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-default tracking-wide shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Slanje...
          </> : "Rezerviraj popravak"}
      </Button>
    </Form>;
}
export default function VisitOurPatisserie() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftColRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightColRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="rezervirajte-termin" ref={sectionRef} className="relative w-full py-24 md:py-32 bg-background overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center pointer-events-none select-none overflow-hidden" aria-hidden="true">
        <span className="text-[15vw] font-default font-bold text-primary/5 whitespace-nowrap tracking-tighter leading-none">
          SERVIS
        </span>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          {}
          <div ref={leftColRef} className={cn("transition-all duration-1000 ease-out", isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            <div className="mb-10">
              <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4">
                Rezervirajte termin
              </h2>
              <div className="w-24 h-1 bg-primary-foreground mb-6" />
              <p className="text-lg text-muted-foreground font-sans leading-relaxed">
                Osigurajte vrhunsku uslugu za svoj romobil. Ispunite obrazac i naš tehnički tim će vas kontaktirati u najkraćem mogućem roku radi potvrde.
              </p>
            </div>

            <FormProvider formKey="reservation_form_home" formId="reservation_form_home" sectionName="Rezervirajte termin" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            visit_date: "",
            guest_count: 1,
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            visit_date: {
              required: true
            },
            guest_count: {
              required: true,
              min: 1,
              max: 20
            }
          }} submitText="Rezerviraj popravak" successMessage="Vaš zahtjev za servis je zaprimljen. Naš tim će vas uskoro kontaktirati radi potvrde termina u ChargeZone servisu.">
              <VisitFormContent />
            </FormProvider>
          </div>

          {}
          <div ref={rightColRef} className={cn("flex flex-col h-full transition-all duration-1000 ease-out delay-200", isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
            {}
            <div className="relative w-full h-[400px] bg-muted border border-border mb-10 overflow-hidden group rounded-sm shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576390/img/map_grayscale_givral.png" alt="Lokacija ChargeZone servisa u Zagrebu" className="w-full h-full object-cover grayscale opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                <div className="bg-primary text-primary-foreground p-3 rounded-full shadow-xl animate-bounce">
                  <MapPin className="w-8 h-8" />
                </div>
                <div className="w-4 h-2 bg-black/20 rounded-[100%] mt-2 blur-[2px]" />
              </div>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 bg-card p-8 border border-border rounded-sm shadow-md">
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-sans font-bold mb-3">
                    Lokacija
                  </h4>
                  <div className="flex items-start gap-3 text-foreground">
                    <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="font-default text-lg leading-snug">
                      ChargeZone Servis Zagreb<br />
                      Ulica Grada Vukovara 123<br />
                      10000 Zagreb, Hrvatska
                    </span>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-sans font-bold mb-3">
                    Kontakt
                  </h4>
                  <div className="space-y-3">
                    <Link to="tel:+38512345678" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group">
                      <Phone className="w-5 h-5 text-primary shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="font-default text-lg">098123456789</span>
                    </Link>
                    <Link to="mailto:info@chargezone.hr" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors group">
                      <Mail className="w-5 h-5 text-primary shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="font-default text-lg">romobili.zg@gmail.com</span>
                    </Link>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm uppercase tracking-widest text-muted-foreground font-sans font-bold mb-3">
                  Radno vrijeme
                </h4>
                <div className="flex items-start gap-3 text-foreground">
                  <Clock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div className="font-default text-lg space-y-1">
                    <p>Pon-Pet: 08:00 - 18:00</p>
                    <p>Subota: 09:00 - 14:00</p>
                    <p className="text-muted-foreground text-sm">Nedjeljom zatvoreno</p>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>;
}